import { useContext, useEffect, useState } from "react";
import { API } from "aws-amplify";
import {
  Box,
  Button,
  Card,
  CheckBox,
  CardBody,
  CardHeader,
  Form,
  FormField,
  Image,
  Layer,
  NameValueList,
  NameValuePair,
  ResponsiveContext,
  Select,
  Text,
  TextInput,
} from "grommet";
import { ListOrganizations } from "../../graphql/queries";
import PodImageModal from "../PodComponents/PodImageModal";
import { getOrganizationAction } from "../../config/organizationActions";
import IntegrationButtons from "./IntegrationButtons";
import TransferToProctor from "./TransferToProctor";
import { resolveImageUrl } from "./imageUtils";
import { isClaimableProctor } from "./sessionUtils";

function sortOrganizations(organizations) {
  return [...organizations].sort((a, b) =>
    (a.name || "").localeCompare(b.name || "", undefined, {
      sensitivity: "base",
    }),
  );
}

function getOrganizationPodId(organization) {
  return String(organization?.pod_id ?? organization?.podId ?? "").trim();
}

function SessionImage({ label, src, onClick }) {
  if (!src) return null;

  return (
    <Box align="center" gap="xsmall">
      <Image
        src={src}
        fit="contain"
        style={{ cursor: "pointer", maxHeight: "160px" }}
        onClick={onClick}
      />
      <Text size="small">{label}</Text>
    </Box>
  );
}

export default function PodSessionInfoCard({
  podSession,
  organization,
  podExamCodes,
  podNotesFormat,
  userEmail,
  claiming,
  onClaim,
  onUnclaim,
  localMeetLink,
  setLocalMeetLink,
  meetLinkLoading,
  onSaveMeetLink,
  meetLoading,
  onGenerateGoogleMeetLink,
  onTransferToProctor,
  onVerifyIdentity,
  onUpdateSessionInfo,
  testerJoinedDisabled = false,
  onTesterJoinedChange,
}) {
  const size = useContext(ResponsiveContext);
  const isNarrow = size === "small";
  const [selectedImage, setSelectedImage] = useState(null);
  const [idPictureUrl, setIdPictureUrl] = useState("");
  const [secondIdPictureUrl, setSecondIdPictureUrl] = useState("");
  const [selfiePictureUrl, setSelfiePictureUrl] = useState("");
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormValues, setEditFormValues] = useState({
    organization: null,
    organizationId: "",
    organizationName: "",
    podID: "",
    testerName: "",
    testerEmail: "",
    testerPhone: "",
  });
  const [organizations, setOrganizations] = useState([]);
  const [organizationOptions, setOrganizationOptions] = useState([]);
  const [loadingOrganizations, setLoadingOrganizations] = useState(false);
  const [savingSessionInfo, setSavingSessionInfo] = useState(false);
  const [sessionInfoError, setSessionInfoError] = useState(null);

  const fields = podSession.fields ?? [];
  const canClaimSession = isClaimableProctor(podSession.proctor);

  const fetchOrganizations = async () => {
    setLoadingOrganizations(true);

    try {
      const { data } = await API.graphql({ query: ListOrganizations });
      const parsed = JSON.parse(data.listOrganizations);
      const items = Array.isArray(parsed.body) ? parsed.body : [];
      const sorted = sortOrganizations(items);

      setOrganizations(sorted);
      setOrganizationOptions(sorted);
    } catch (error) {
      console.error("Error fetching organizations:", error);
      setOrganizations([]);
      setOrganizationOptions([]);
      setSessionInfoError("Unable to load organizations.");
    } finally {
      setLoadingOrganizations(false);
    }
  };

  const openEditModal = () => {
    setEditFormValues({
      organization: null,
      organizationId: podSession.organizationId || "",
      organizationName: podSession.organizationName || "",
      podID: podSession.podID || podSession.podId || "",
      testerName: podSession.testerName || "",
      testerEmail: podSession.testerEmail || "",
      testerPhone: podSession.testerPhone || "",
    });
    setSessionInfoError(null);
    setShowEditModal(true);
    fetchOrganizations();
  };

  const closeEditModal = () => {
    if (savingSessionInfo) return;

    setShowEditModal(false);
    setSessionInfoError(null);
    setOrganizationOptions(organizations);
  };

  const handleOrganizationSearch = (searchTerm) => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    if (!normalizedSearch) {
      setOrganizationOptions(organizations);
      return;
    }

    setOrganizationOptions(
      organizations.filter((organizationOption) =>
        (organizationOption.name || "").toLowerCase().includes(normalizedSearch),
      ),
    );
  };

  const handleEditFormChange = (nextValue) => {
    setEditFormValues((prev) => ({
      ...prev,
      ...nextValue,
    }));
  };

  const handleOrganizationSelect = ({ option }) => {
    setEditFormValues((prev) => ({
      ...prev,
      organization: option,
      organizationId: option?.id || "",
      organizationName: option?.name || "",
      podID: getOrganizationPodId(option),
    }));
  };

  const handleSaveSessionInfo = async ({ value }) => {
    if (!onUpdateSessionInfo) return;

    setSavingSessionInfo(true);
    setSessionInfoError(null);

    try {
      await onUpdateSessionInfo({
        organizationId: String(value.organizationId ?? "").trim(),
        organizationName: value.organizationName?.trim() || "",
        podID: String(value.podID ?? "").trim(),
        testerName: value.testerName?.trim() || "",
        testerEmail: value.testerEmail?.trim() || "",
        testerPhone: value.testerPhone?.trim() || "",
      });
      setShowEditModal(false);
      window.location.reload();
    } catch (error) {
      console.error(error);
      setSessionInfoError("Unable to update session information.");
    } finally {
      setSavingSessionInfo(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    async function loadImages() {
      const [idUrl, secondIdUrl, selfieUrl] = await Promise.all([
        resolveImageUrl(podSession.idPictureUrlPath),
        resolveImageUrl(podSession.secondIdPictureUrlPath),
        resolveImageUrl(podSession.selfiePictureUrlPath),
      ]);

      if (cancelled) return;

      setIdPictureUrl(idUrl);
      setSecondIdPictureUrl(secondIdUrl);
      setSelfiePictureUrl(selfieUrl);
    }

    loadImages();

    return () => {
      cancelled = true;
    };
  }, [
    podSession.idPictureUrlPath,
    podSession.secondIdPictureUrlPath,
    podSession.selfiePictureUrlPath,
  ]);

  return (
    <>
      <Card fill height={{ min: "large" }}>
        <CardHeader
          pad="medium"
          border={{ side: "bottom", color: "gray", size: "xsmall" }}
        >
          <Box
            direction={isNarrow ? "column" : "row"}
            align={isNarrow ? "start" : "center"}
            justify="between"
            fill
            gap="medium"
          >
            <Box>
              <Text weight="bold" size="large">
                {podSession.testerName}
              </Text>
              <Text weight="bold" size="medium">
                {podSession.organizationName}
              </Text>
            </Box>
            <Box align={isNarrow ? "start" : "end"} gap="small">
              {canClaimSession ? (
                <Button
                  label="Claim Session"
                  primary
                  color="black"
                  onClick={onClaim}
                  disabled={claiming || !userEmail}
                />
              ) : (
                <Box align={isNarrow ? "start" : "end"} gap="xsmall">
                  <Text weight="bold" size="medium">
                    Proctor: {podSession.proctor}
                  </Text>
                </Box>
              )}
              <Button
                label="Edit Session Information"
                size="small"
                onClick={openEditModal}
                disabled={!onUpdateSessionInfo}
              />
            </Box>
          </Box>
        </CardHeader>

        <CardBody pad="medium" gap="medium">
          <NameValueList>
            <NameValuePair name="Email">
              <Text>{podSession.testerEmail}</Text>
            </NameValuePair>
            <NameValuePair name="Phone">
              <Text>{podSession.testerPhone}</Text>
            </NameValuePair>
            {podSession.verified_by && (
              <NameValuePair name="ID Verified By">
                <Text>{podSession.verified_by}</Text>
              </NameValuePair>
            )}
            {fields.map((field) => (
              <NameValuePair name={field.key} key={field.key}>
                <Text>{field.value}</Text>
              </NameValuePair>
            ))}
            <NameValuePair name="Google Meet Link">
              <Box direction="row" gap="small" align="center">
                <TextInput
                  value={localMeetLink}
                  onChange={(event) => setLocalMeetLink(event.target.value)}
                  placeholder="Enter Google Meet link"
                  size="small"
                />
                <Button
                  label="Save and Open"
                  size="small"
                  primary
                  onClick={onSaveMeetLink}
                  disabled={meetLinkLoading}
                />
                {meetLinkLoading && <Text size="small">Saving...</Text>}
              </Box>
            </NameValuePair>
            <NameValuePair name="Tester has joined Google Meet">
              <CheckBox
                toggle
                label="Tester has joined Google Meet"
                checked={podSession.hasTesterJoined === true}
                disabled={testerJoinedDisabled || !onTesterJoinedChange}
                onChange={(event) =>
                  onTesterJoinedChange?.(event.target.checked)
                }
              />
            </NameValuePair>
          </NameValueList>

          <Box direction="row" wrap justify="center" gap="medium">
            <SessionImage
              label="ID Picture (click to enlarge)"
              src={idPictureUrl}
              onClick={() =>
                setSelectedImage({ url: idPictureUrl, type: "ID Picture" })
              }
            />
            {podSession.secondIdPictureUrlPath && (
              <SessionImage
                label="Second ID Picture (click to enlarge)"
                src={secondIdPictureUrl}
                onClick={() =>
                  setSelectedImage({
                    url: secondIdPictureUrl,
                    type: "Second ID Picture",
                  })
                }
              />
            )}
            <SessionImage
              label="Selfie Picture (click to enlarge)"
              src={selfiePictureUrl}
              onClick={() =>
                setSelectedImage({
                  url: selfiePictureUrl,
                  type: "Selfie Picture",
                })
              }
            />
          </Box>

          <TransferToProctor
            podSession={podSession}
            onTransfer={onTransferToProctor}
          />

          <IntegrationButtons
            podSession={podSession}
            organization={organization}
            podExamCodes={podExamCodes}
            podNotesFormat={podNotesFormat}
            userEmail={userEmail}
          />

          <Box gap="small">
            {(!podSession.verified_by || podSession.verified_by === "") && (
              <Button
                label="I have verified the ID and selfie"
                primary
                color="status-ok"
                onClick={onVerifyIdentity}
                disabled={!userEmail}
              />
            )}
            {meetLoading && <Text>Loading...</Text>}
            {!meetLoading &&
              (() => {
                const action = getOrganizationAction(podSession.organizationId);
                if (!action.showCondition(podSession)) return null;

                return (
                  <Button
                    label={action.label}
                    primary
                    color="black"
                    onClick={() => {
                      if (action.action === "redirect") {
                        window.open(action.url, "_blank");
                      } else if (action.action === "generate") {
                        onGenerateGoogleMeetLink();
                      }
                    }}
                    disabled={meetLoading}
                  />
                );
              })()}
          </Box>
        </CardBody>
      </Card>

      <PodImageModal
        selectedImage={selectedImage}
        closeModal={() => setSelectedImage(null)}
      />

      {showEditModal && (
        <Layer
          position="center"
          modal
          onEsc={closeEditModal}
          onClickOutside={closeEditModal}
        >
          <Box pad="medium" gap="medium" width="medium">
            <Text weight="bold" size="large">
              Edit Session Information
            </Text>
            <Form
              value={editFormValues}
              onChange={handleEditFormChange}
              onSubmit={handleSaveSessionInfo}
              onReset={closeEditModal}
            >
              <FormField label="Organization" name="organization">
                <Select
                  name="organization"
                  placeholder="Select organization"
                  options={organizationOptions}
                  labelKey="name"
                  valueKey="id"
                  onSearch={handleOrganizationSearch}
                  onChange={handleOrganizationSelect}
                  searchPlaceholder="Search organizations"
                  disabled={loadingOrganizations}
                />
              </FormField>
              <FormField label="Organization Name" name="organizationName">
                <TextInput name="organizationName" />
              </FormField>
              <FormField label="Tester Name" name="testerName">
                <TextInput name="testerName" />
              </FormField>
              <FormField label="Tester Email" name="testerEmail">
                <TextInput name="testerEmail" type="email" />
              </FormField>
              <FormField label="Tester Phone" name="testerPhone">
                <TextInput name="testerPhone" type="tel" />
              </FormField>
              {sessionInfoError && (
                <Text size="small" color="status-critical">
                  {sessionInfoError}
                </Text>
              )}
              <Box direction="row" gap="small" justify="end">
                <Button
                  label="Cancel"
                  type="reset"
                  disabled={savingSessionInfo}
                />
                <Button
                  label={savingSessionInfo ? "Saving..." : "Save"}
                  type="submit"
                  primary
                  color="black"
                  disabled={savingSessionInfo}
                />
              </Box>
            </Form>
          </Box>
        </Layer>
      )}
    </>
  );
}
